/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getIcon,
  isEmptyOrNull,
  SLRLabelIcon,
  SLRModal,
  SLRPageHead,
  SLRSpinner,
  SLRTable,
  SLRTooltip
} from '@SLR/shared-library';
import { DraftStatus, OrganizationDraft } from '@SLR/user-service-full-sdk';
import { default as MarkdownPreview } from '@uiw/react-markdown-preview';
import { useGetOrganizationDrafts } from 'hooks/organization-draft/useGetOrganizationDrafts';
import { Empty } from 'pages/organizations';
import { FC, useCallback, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getLastCommentDiv, getOnboardingType } from '../util/draft';

import './RequestsPage.scss';

export const RequestsPage: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'requestsPage'
  });
  const { data: drafts, isPending } = useGetOrganizationDrafts();

  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const handleShowInfoModal = (show: boolean) => setShowInfoModal(show);

  const navigate = useNavigate();

  const handleOpenDraft = useCallback(
    (id?: string) => !isEmptyOrNull(id) && navigate(id),
    [navigate]
  );

  if (isPending) {
    return <SLRSpinner />;
  }

  return (
    <>
      {showInfoModal && (
        <SLRModal
          isOpen
          size="lg"
          fullscreen="sm-down"
          className="solutions-page-modal"
          title={t('info.title')}
          onClose={() => handleShowInfoModal(false)}
        >
          <h2 className="pb-3">{t('info.content.title')}</h2>

          <p>
            <Trans t={t} i18nKey="info.content.message" />
          </p>
        </SLRModal>
      )}

      <Container className="requests-page table-responsive-md">
        <SLRPageHead
          className="pb-5"
          labels={{
            title: (
              <div className="d-flex flex-row gap-3">
                <span className="requests-title">{t('registerRequests')}</span>
                <SLRLabelIcon
                  width="36"
                  icon={getIcon('fal', 'info-circle')}
                  onClick={() => handleShowInfoModal(true)}
                />
              </div>
            ),
            description: t('description')
          }}
        />
        <Row>
          <Col xs={12}>
            <SLRTable<OrganizationDraft>
              emptyView={<Empty labels={t('empty', { returnObjects: true })} />}
              sortIcon={getIcon('fal', 'long-arrow-up')}
              header={{ title: t('requests') }}
              columns={[
                {
                  label: t('name'),
                  accessor: 'name',
                  sortable: true
                },
                {
                  label: t('statusLabel'),
                  accessor: 'status',
                  sortable: true,
                  cellFn: ({ value, data }) => {
                    const hasComments =
                      !isEmptyOrNull(data.comment) &&
                      value === DraftStatus.Pending;

                    const lastComment = getLastCommentDiv(
                      'comment',
                      data.comment
                    );

                    return (
                      <>
                        {t(
                          `status.${hasComments ? 'PROGRESS' : (value as DraftStatus)}`
                        )}
                        {hasComments && (
                          <SLRTooltip
                            content={
                              <MarkdownPreview
                                className="markdown-style p-2"
                                source={
                                  '<span style="font-size:12px">Letztes Kommentar:</span>\n' +
                                  (lastComment ?? 'not found')
                                }
                                wrapperElement={{ 'data-color-mode': 'light' }}
                              />
                            }
                            trigger={['hover', 'focus']}
                            placement="top"
                          >
                            <FontAwesomeIcon
                              icon={getIcon('fal', 'message-lines')}
                              className="ms-2"
                            />
                          </SLRTooltip>
                        )}
                      </>
                    );
                  }
                },
                {
                  label: t('type'),
                  accessor: 'type',
                  sortable: true,
                  cellFn: ({ data }) => {
                    const draftType = getOnboardingType(
                      data?.type,
                      data?.geoAreaId
                    );
                    return t(`requestType.${draftType}`);
                  }
                },
                {
                  label: t('lastUpdated'),
                  accessor: 'lastUpdated',
                  sortable: true,
                  centered: true,
                  cellFn: ({ value }: { value: string }) =>
                    new Date(value).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })
                }
              ]}
              data={drafts}
              responsive="md"
              onRowClick={({ id }) => handleOpenDraft(id)}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};
